import { CellContext } from '@tanstack/react-table';
import { getTableMetaHelper } from '@round/ui-kit';
import Skeleton from 'react-loading-skeleton';
import { Button, Dropdown, Tooltip, assets } from '@round/whitelabel-ui';
import { usePrefetchPoolsListInfinite } from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { isNumber } from 'utility/utility';
import { getDefaultPoolSelectParams } from '../../AddCreatorsFromPoolModal/usePoolCombobox';
import { AddCreatorsSource, CampaignTableRow } from '../ProjectCampaignsTable';
import styles from './AddCreatorsCell.module.css';

const { UserPlus: UserPlusIcon, UserGroup: UserGroupIcon, ChevronDown: ChevronDownIcon } = assets;

type Meta = {
    isLoading: boolean;
    onAddCreatorsToCampaign?: (campaign: CampaignTableRow, source: AddCreatorsSource) => void;
};
const getTableMeta = getTableMetaHelper<Meta>();

const AddCreatorsCell = ({ row: { original }, table }: CellContext<CampaignTableRow, unknown>) => {
    const { isLoading, onAddCreatorsToCampaign } = getTableMeta(table);
    const { selectedClientId } = useClientSelect();
    const prefetchPoolsList = usePrefetchPoolsListInfinite();

    // Opening the menu is a reliable signal that the pool sheet is one click away, so the list loads
    // while the user reads the two options rather than after they pick one.
    const prefetchPools = () => {
        if (!isNumber(selectedClientId)) {
            return;
        }

        void prefetchPoolsList(getDefaultPoolSelectParams(selectedClientId));
    };

    if (original.managed_by !== 'creatorbase') {
        return null;
    }

    if (isLoading) {
        return <Skeleton />;
    }

    return (
        <Dropdown>
            {({ isOpen, setIsOpen, setAnchorElement }) => (
                <>
                    <Tooltip.Root>
                        {(anchor, setAnchor) => (
                            <>
                                <Button
                                    ref={(element) => {
                                        setAnchor(element);
                                        setAnchorElement(element);
                                    }}
                                    appearance="ghost"
                                    className={styles.button}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isOpen) {
                                            prefetchPools();
                                        }

                                        setIsOpen(!isOpen);
                                    }}
                                >
                                    <UserPlusIcon className={styles.icon} />
                                    <ChevronDownIcon className={styles.chevron} />
                                </Button>
                                <Tooltip anchorElement={anchor}>
                                    <Tooltip.Body>Add creators</Tooltip.Body>
                                </Tooltip>
                            </>
                        )}
                    </Tooltip.Root>

                    <Dropdown.Menu>
                        <Dropdown.Item
                            icon={UserPlusIcon}
                            className={styles.menuItem}
                            onClick={() => {
                                onAddCreatorsToCampaign?.(original, 'explore');
                                setIsOpen(false);
                            }}
                        >
                            Creator explore
                        </Dropdown.Item>

                        <Dropdown.Item
                            icon={UserGroupIcon}
                            className={styles.menuItem}
                            onClick={() => {
                                onAddCreatorsToCampaign?.(original, 'pool');
                                setIsOpen(false);
                            }}
                        >
                            Creator pools
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </>
            )}
        </Dropdown>
    );
};

export default AddCreatorsCell;
